/**
 * One invalid-link screen, not several: the server answers unknown, expired, cancelled and
 * locked-out identically, so explaining which would be inventing information.
 */

import { AlertCircle, ShieldCheck } from 'lucide-react';

type AgentEnteredConsentResultVariant = 'consented' | 'invalidLink' | 'alreadyConsented';

interface AgentEnteredConsentResultProps {
  variant: AgentEnteredConsentResultVariant;
  advisorFirstName?: string;
}

export function AgentEnteredConsentResult({
  variant,
  advisorFirstName,
}: AgentEnteredConsentResultProps) {
  if (variant === 'invalidLink') {
    return (
      <div className="text-center">
        <div className="mb-6 flex justify-center">
          <div className="flex size-16 items-center justify-center rounded-full bg-red-50">
            <AlertCircle
              className="size-9 text-red-600"
              strokeWidth={2}
              aria-hidden="true"
            />
          </div>
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-950">
          This link isn&apos;t valid
        </h1>
        <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
          It may have expired, or a newer one may have been sent. Ask the person who contacted you
          to send a fresh link.
        </p>
      </div>
    );
  }

  const heading = variant === 'alreadyConsented' ? "You've already done this" : 'Thank you';
  const body =
    variant === 'alreadyConsented'
      ? 'You have already given permission for this. There is nothing else you need to do.'
      : `${
          advisorFirstName ? `${advisorFirstName} can` : 'They can'
        } now get started. You may close this page.`;

  return (
    <div className="text-center">
      <div className="mb-6 flex justify-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-green-50">
          <ShieldCheck
            className="size-9 text-green-600"
            strokeWidth={2}
            aria-hidden="true"
          />
        </div>
      </div>
      <h1 className="text-2xl font-bold tracking-tight text-gray-950">{heading}</h1>
      <p className="text-muted-foreground mx-auto mt-3 max-w-sm text-base leading-relaxed">
        {body}
      </p>
    </div>
  );
}
